import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Button, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import type { GridColumnVisibilityModel, GridEventListener, GridRowModesModel } from "@mui/x-data-grid-premium";
import { GridRowEditStopReasons, GridRowModes } from "@mui/x-data-grid-premium";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { createApiResponse, withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { FileDownloadInfo } from "@entrilia/shared/api/fileTypes";
import type { EntriliaProduct } from "@entrilia/shared/api/types";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import PreviewFileDialog from "@entrilia/shared/components/previewFile/PreviewFileDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { makeOpaqueTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import { getChangedFields, isEmptyObject } from "@entrilia/shared/utilities/objectHelper";
import adminApi from "../../../../../../api/adminApi";
import type {
  DataImportChangesFilterValue,
  DataImportState,
  FileDataImport,
  ImportFieldDefinition,
} from "../../../../../../api/types/dataImportTypes";
import useClientContext from "../../../../../../context/useClientContext";
import useResourceChanged from "../../../../../../hooks/useResourceChanged";
import GridPaginationFooter from "../../../../../common/GridPaginationFooter";
import {
  getPermissionsToManageImport,
  isImportApplicationAllowed,
  isImportEditable,
} from "../../../importDataStateHelper";
import DataImportChangesListFilter from "./DataImportChangesFilter";
import DataImportChangesGridToolbar from "./DataImportChangesGridToolbar";
import { DataImportChangesListActionsContextProvider } from "./DataImportChangesListActionsContextProvider";
import { createNewRow, getColumnDefinitions, getNewValuesObject } from "./dataImportChangesGridProvider";
import type { DataImportChangeRowModel } from "./dataImportChangesGridTypes";
import {
  changeFilterAction,
  changePageAction,
  changeRowsPerPageAction,
  getInitialState,
  toggleShowChangesAction,
  updateTotalsAction,
} from "./dataImportChangesListState";

interface Props {
  dataImportId: string;
  dataImportState: DataImportState;
  productAreas: EntriliaProduct[];
  importFileInfo: FileDataImport;
  importFieldDefinitions: ImportFieldDefinition[];
  isOperationRunning: boolean;
  readOnly?: boolean;
}

const addDataImportChange = withErrorHandling(adminApi.addDataImportChange);
const updateDataImportChange = withErrorHandling(adminApi.updateDataImportChange);
const excludeDataImportChange = withErrorHandling(adminApi.excludeDataImportChange);
const revertExcludedDataImportChange = withErrorHandling(adminApi.revertExcludedDataImportChange);
const excludeChangesWithErrors = withErrorHandling(adminApi.excludeChangesWithErrors);
const getFileContainerFileDownloadInfo = withErrorHandling(adminApi.getFileContainerFileDownloadInfo);

const hiddenActionsColumnModel: GridColumnVisibilityModel = { gridActions: false };

const DataImportChangesList = ({
  dataImportId,
  dataImportState,
  productAreas,
  importFileInfo,
  importFieldDefinitions,
  isOperationRunning,
  readOnly,
}: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { dictionaries, hasPermissions } = useClientContext();
  const { sendNotificationError } = useNotificationContext();

  const [state, setState] = useState(() =>
    getInitialState(readOnly ? undefined : (searchParams.get("filter") as DataImportChangesFilterValue) || undefined)
  );

  // Kept out of `changesResp` so a reload in flight when a row is added cannot drop it.
  const [newRows, setNewRows] = useState<DataImportChangeRowModel[]>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [isOperationInProgress, setIsOperationInProgress] = useState(false);
  const [filePreviewDialogData, setFilePreviewDialogData] = useState<FileDownloadInfo>();

  const getChanges = useCallback(
    () =>
      adminApi.searchDataImportChanges(dataImportId, {
        fileDataCatalogueId: importFileInfo.sourceFileDataCatalogueId,
        filter: state.filter,
        paging: {
          totals: true,
          page: state.page,
          size: state.rowsPerPage,
        },
      }),
    [dataImportId, importFileInfo.sourceFileDataCatalogueId, state.filter, state.page, state.rowsPerPage]
  );

  const getTotals = useCallback(
    () => adminApi.getDataImportChangesTotals(dataImportId, importFileInfo.sourceFileDataCatalogueId),
    [dataImportId, importFileInfo.sourceFileDataCatalogueId]
  );

  const [
    changesResp,
    fetchChangesError,
    { isFetching: isFetchingChanges, fetch: fetchChanges, fetchSilent: fetchChangesSilent },
  ] = useFetch(getChanges, {
    onSetData: (data) => {
      if (data.totalPages !== undefined && data.total !== undefined) {
        setState(updateTotalsAction(data.total, data.totalPages));
      }
    },
  });

  const [totalsResp, totalsError, { fetch: fetchTotals, fetchSilent: fetchTotalsSilent }] = useFetch(getTotals);

  const getLookupOptions = useCallback(
    () =>
      importFieldDefinitions.some((d) => d.type === "Lookup")
        ? adminApi.getLookupOptions(importFileInfo.importObjectTypeTarget)
        : Promise.resolve(createApiResponse({})),
    [importFieldDefinitions, importFileInfo.importObjectTypeTarget]
  );

  const [lookupOptions, lookupOptionsError, { isFetching: isFetchingLookupOptions }] = useFetch(getLookupOptions);

  const fileContainerId = importFileInfo.fileContainerIds?.[0];

  const isGridEditable = useMemo(
    () =>
      !readOnly &&
      !isOperationRunning &&
      isImportEditable(dataImportState) &&
      hasPermissions(getPermissionsToManageImport(productAreas)),
    [dataImportState, hasPermissions, isOperationRunning, productAreas, readOnly]
  );

  const columns = useMemo(
    () =>
      getColumnDefinitions(
        importFieldDefinitions,
        dictionaries,
        lookupOptions ?? {},
        isGridEditable,
        state.showChanges,
        fileContainerId,
        rowModesModel
      ),
    [
      dictionaries,
      lookupOptions,
      importFieldDefinitions,
      fileContainerId,
      isGridEditable,
      rowModesModel,
      state.showChanges,
    ]
  );

  useResourceChanged("DataImport", dataImportId, {
    onChanged: async () => {
      const [changesError, totalsFetchError] = await Promise.all([fetchChangesSilent(), fetchTotalsSilent()]);
      if (changesError) {
        logError(changesError, "[DataImportChangesList] fetchChangesSilent");
      }
      if (totalsFetchError) {
        logError(totalsFetchError, "[DataImportChangesList] fetchTotalsSilent");
      }
    },
    paused: isOperationInProgress || Object.values(rowModesModel).some(({ mode }) => mode === GridRowModes.Edit),
  });

  const rows = useMemo<DataImportChangeRowModel[]>(
    () => [...newRows, ...(changesResp?.items ?? [])],
    [newRows, changesResp]
  );

  const fetchError = fetchChangesError || totalsError || lookupOptionsError;
  const isLoading = isFetchingChanges || isFetchingLookupOptions || isOperationInProgress;

  if (fetchError) {
    logError(fetchError, "[DataImportChangesList] searchDataImportChanges");
    return <DataLoadingFailed title="Failed to load changes" />;
  }

  const leftPinnedColumns = [
    "rowNumber",
    "action",
    ...importFieldDefinitions.filter((d) => d.isObjectIdentifierField).map((d) => d.name),
  ];

  const handleShowChangesToggle = () => {
    setState(toggleShowChangesAction());
  };

  const handleExcludeChange = async (id: string) => {
    if (newRows.some((row) => row.id === id)) {
      setNewRows((prev) => prev.filter((row) => row.id !== id));
      return;
    }

    setIsOperationInProgress(true);
    const [result, error] = await excludeDataImportChange(dataImportId, id);
    setIsOperationInProgress(false);

    if (error || !result) {
      logError(error, "[DataImportChangesList] excludeDataImportChange");
      sendNotificationError(error?.message || "Failed to exclude the change");
      return;
    }

    fetchChanges();
    fetchTotals();
  };

  const handleRevertExcludedChange = async (id: string) => {
    setIsOperationInProgress(true);
    const [result, error] = await revertExcludedDataImportChange(dataImportId, id);
    setIsOperationInProgress(false);

    if (error || !result) {
      logError(error, "[DataImportChangesList] revertExcludedDataImportChange");
      sendNotificationError(error?.message || "Failed to revert the excluded change");
      return;
    }

    fetchChanges();
    fetchTotals();
  };

  const handleExcludeAllRowsWithErrors = async () => {
    setIsOperationInProgress(true);
    const [result, error] = await excludeChangesWithErrors(dataImportId);
    setIsOperationInProgress(false);

    if (error || !result) {
      logError(error, "[DataImportChangesList] excludeChangesWithErrors");
      sendNotificationError(error?.message || "Failed to exclude all rows with errors");
      return;
    }

    fetchChanges();
    fetchTotals();
  };

  const handlePreviewFileContainerFile = async (fileContainerId: string, fileName: string) => {
    const [fileDownloadInfo, error] = await getFileContainerFileDownloadInfo(fileContainerId, fileName);
    if (error) {
      logError(error, "[DataImportChangesList] getFileContainerFileDownloadInfo");
      sendNotificationError(`Could not access the file '${fileName}' in File Container`);
      return;
    }

    setFilePreviewDialogData(fileDownloadInfo);
  };

  const handleCancelEditing = (id: string) => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    }));

    setNewRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleApplyChanges = (id: string) => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.View },
    }));
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleAddRowClick = () => {
    const id = generateGuid();
    const newItem = createNewRow(id, dataImportId, importFileInfo.sourceFileDataCatalogueId, importFieldDefinitions);
    setNewRows((prev) => [newItem, ...prev]);

    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit },
    }));
  };

  const processRowUpdate = async (updatedRow: DataImportChangeRowModel, currentRow: DataImportChangeRowModel) => {
    if (updatedRow.isNew) {
      const changes = getNewValuesObject(updatedRow);

      setIsOperationInProgress(true);
      const [row, error] = await addDataImportChange(dataImportId, {
        dataImportFileCatalogueId: importFileInfo.sourceFileDataCatalogueId,
        changes,
      });
      setIsOperationInProgress(false);

      if (error) {
        logError(error, "[DataImportChangesList] addDataImportChange");
        sendNotificationError(error.message);
        return updatedRow;
      }

      setNewRows((prev) => prev.filter(({ id }) => id !== updatedRow.id));
      fetchChanges();
      fetchTotals();

      return { ...row, isNew: false };
    }

    const currentChangesObj = getNewValuesObject(currentRow);
    const updatedChangesObj = getNewValuesObject(updatedRow);

    const changes = getChangedFields(currentChangesObj, updatedChangesObj);
    if (isEmptyObject(changes)) {
      return currentRow;
    }

    setIsOperationInProgress(true);
    const [row, error] = await updateDataImportChange(dataImportId, updatedRow.id, { changes });
    setIsOperationInProgress(false);

    if (error) {
      logError(error, "[DataImportChangesList] updateDataImportChange");
      sendNotificationError(error.message);
      return currentRow;
    }

    fetchTotals();
    fetchChanges();

    return { ...row, isNew: false };
  };

  const handleRowEditStop: GridEventListener<"rowEditStop"> = ({ id, reason }, e) => {
    if (reason === GridRowEditStopReasons.rowFocusOut) {
      e.defaultMuiPrevented = true;
      handleCancelEditing(id.toString());
    }
  };

  const handleFilterChange = (newValue: DataImportChangesFilterValue | undefined) => {
    setState(changeFilterAction(newValue));
    if (readOnly) {
      return;
    }
    if (newValue !== undefined) {
      setSearchParams({ filter: newValue });
    } else {
      setSearchParams({});
    }
  };

  return (
    <Stack sx={{ height: "100%", pt: 2.5, px: 3 }}>
      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2.5 }}
      >
        <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <DataImportChangesListFilter
            value={state.filter}
            onChange={handleFilterChange}
            allCount={totalsResp?.allCount}
            changesCount={totalsResp?.changesCount}
            validationErrorsCount={totalsResp?.validationErrorsCount}
            applicationErrorsCount={totalsResp?.applicationErrorsCount}
            excludedCount={totalsResp?.excludedCount}
          />
          <RecordCounter
            records={rows.length}
            totalRecords={(state.totalFilteredItems ?? 0) + newRows.length}
            hide={isLoading || state.totalFilteredItems === 0}
          />
        </Stack>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <FormControlLabel
            control={<Switch checked={state.showChanges} onChange={handleShowChangesToggle} />}
            label={<Typography>Show changes</Typography>}
          />
          {!readOnly && (
            <Button
              disabled={
                !totalsResp?.validationErrorsCount ||
                isLoading ||
                isOperationRunning ||
                !isImportApplicationAllowed(dataImportState)
              }
              color="error"
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={handleExcludeAllRowsWithErrors}
            >
              Exclude all rows with errors
            </Button>
          )}
        </Stack>
      </Stack>
      <DataImportChangesListActionsContextProvider
        onExcludeChange={handleExcludeChange}
        onRevertExcludedChange={handleRevertExcludedChange}
        onCancelEditing={handleCancelEditing}
        onApplyChanges={handleApplyChanges}
        onPreviewFileContainerFile={handlePreviewFileContainerFile}
      >
        <DataGrid<DataImportChangeRowModel>
          rows={rows}
          columns={columns}
          loading={isLoading}
          pinnedColumns={{ left: leftPinnedColumns, right: ["gridActions"] }}
          columnVisibilityModel={readOnly && !fileContainerId ? hiddenActionsColumnModel : undefined}
          disableColumnReorder
          showCellVerticalBorder
          showColumnVerticalBorder
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          hideFooter={false}
          slots={{
            toolbar: () =>
              isGridEditable ? (
                <DataImportChangesGridToolbar
                  onAddRowClick={handleAddRowClick}
                  addRowDisabled={state.filter !== undefined}
                />
              ) : null,
            footer: () =>
              state.totalFilteredPages !== undefined ? (
                <GridPaginationFooter
                  totalPages={state.totalFilteredPages}
                  rowsPerPage={state.rowsPerPage}
                  pageIndex={state.page}
                  onPageIndexChange={(value) => setState(changePageAction(value))}
                  onRowsPerPageChange={(value) => setState(changeRowsPerPageAction(value))}
                />
              ) : null,
          }}
          containerSx={{ flex: 1, minHeight: 0 }}
          sx={(t) => ({
            borderColor: t.palette.divider,
            ".MuiDataGrid-columnHeaders": {
              borderTop: `1px solid ${t.palette.divider}`,
              ".MuiDataGrid-columnHeader": {
                ":first-of-type": {
                  borderLeft: `1px solid ${t.palette.divider}`,
                },
              },
            },
            ".MuiDataGrid-row": {
              ".MuiDataGrid-cell": {
                ":first-of-type": {
                  borderLeft: `1px solid ${t.palette.divider}`,
                },
              },
              "& .row-number-cell": {
                backgroundColor: t.palette.background.grey,
                color: t.palette.text.secondary,
              },
              "& .row-number-cell-error": {
                backgroundColor: makeOpaqueTintedBackgroundFromColor(
                  t,
                  t.palette.error.main,
                  t.palette.background.grey
                ),
                color: t.palette.text.secondary,
              },
              "& .change-value-cell-error": {
                backgroundColor: makeOpaqueTintedBackgroundFromColor(
                  t,
                  t.palette.error.main,
                  t.palette.background.paper
                ),
              },
              "& .excluded-row-cell": {
                color: t.palette.text.disabled,
              },
            },
            ".MuiDataGrid-columnHeader": {
              backgroundColor: t.palette.background.grey,
            },
          })}
        />

        {filePreviewDialogData && (
          <PreviewFileDialog
            url={filePreviewDialogData.downloadUrl}
            fileName={filePreviewDialogData.fileName}
            onClose={() => setFilePreviewDialogData(undefined)}
          />
        )}
      </DataImportChangesListActionsContextProvider>
    </Stack>
  );
};

export default DataImportChangesList;
